import { Check, Search } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Modal from '@/components/shared/Modal';
import Spinner from '@/components/shared/Spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import apiService from '@/services/apiService';
import { isValidKvKNumber } from '@/services/validators';
import type { KVKCompany } from '@/types/api';

interface SubmitStatus {
	type: 'success' | 'error' | 'info' | '';
	message: string;
}

interface ModalKvKProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onAfterSubmit?: () => void;
}

const ModalKvK = ({ open, onOpenChange, onAfterSubmit }: ModalKvKProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { appData, setAppData } = useAppContext();
	const hasAutoSearched = useRef(false);

	const [searchError, setSearchError] = useState<string>();
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });
	const [searchQuery, setSearchQuery] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState<KVKCompany[]>([]);

	// Helper: Close modal
	const closeModal = () => {
		onOpenChange(false);
	};

	// Helper: Handle API errors
	const handleApiError = (error: unknown, message: string) => {
		setSubmitStatus({ type: 'error', message });
		console.warn(error);
	};

	// Submit KVK number to API and close modal
	const submitKvKNumber = async (kvkNumber: string) => {
		if (!id) {
			setSearchError(t('modals.kvk.noIdError'));
			return;
		}

		setIsSearching(true);
		setSubmitStatus({ type: '', message: '' });

		try {
			const data = await apiService.coc.update(id, { coc: kvkNumber });
			setAppData(data);
			onAfterSubmit?.();
			closeModal();
		} catch (error) {
			handleApiError(error, t('common.error'));
		} finally {
			setIsSearching(false);
		}
	};

	// Perform company name search
	const performSearch = useCallback(
		async (query: string) => {
			if (!query.trim() || !id) {
				setSearchError(t('modals.kvk.inputError'));
				return;
			}

			setIsSearching(true);
			setSearchError(undefined);
			setSubmitStatus({ type: '', message: '' });
			setSearchResults([]);

			try {
				const results = await apiService.coc.search(id, { name: query });
				setSearchResults(results.companies);

				if (results.companies.length === 0) {
					setSubmitStatus({
						type: 'info',
						message: t('modals.kvk.noResults', { query })
					});
				}
			} catch (error) {
				setSearchResults([]);
				setSearchError(t('modals.kvk.searchError'));
				console.warn(error);
			} finally {
				setIsSearching(false);
			}
		},
		[id, t]
	);

	// Handle company selection from search results
	const handleSelectCompany = (company: KVKCompany) => {
		submitKvKNumber(company.kvk_nummer);
	};

	// Handle form submission - route to search or direct submit
	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const query = searchQuery.trim();
		if (!query) {
			setSearchError(t('modals.kvk.inputError'));
			return;
		}

		if (isValidKvKNumber(query)) {
			await submitKvKNumber(query);
		} else {
			await performSearch(query);
		}
	};

	// Auto-search when modal opens
	useEffect(() => {
		if (open) {
			const companyName = (appData?.company_info as { company_name?: string })?.company_name;
			if (companyName && !hasAutoSearched.current) {
				hasAutoSearched.current = true;
				setSearchQuery(companyName);
				setTimeout(() => performSearch(companyName), 100);
			}
		} else {
			// Reset when modal closes
			hasAutoSearched.current = false;
		}
	}, [open, appData?.company_info, performSearch]);

	return (
		<Modal
			id="modalKvK"
			open={open}
			onOpenChange={onOpenChange}
			size="lg"
			backdrop="static"
			closeButton={false}
			keyboard={false}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-3">{t('modals.kvk.title')}</div>
				<p className="mb-6 text-muted-foreground leading-relaxed">
					<span className="sm:inline-block mr-1">{t('modals.kvk.subtitle')}</span>
					<span className="sm:inline-block">{t('modals.kvk.introduction')}</span>
				</p>

				<div className="max-w-2xl mx-auto text-left">
					<form onSubmit={handleSubmit} noValidate className="pb-10">
						<Field data-invalid={!!searchError}>
							<FieldLabel htmlFor="searchQuery" className="sr-only">
								{t('modals.kvk.inputLabel')}
							</FieldLabel>
							<div className="flex">
								<Input
									type="text"
									id="searchQuery"
									placeholder={t('modals.kvk.unifiedSearchPlaceholder')}
									value={searchQuery}
									onChange={(e) => {
										const newValue = e.target.value;
										setSearchQuery(newValue);

										// Clear errors when user starts typing
										if (searchError) setSearchError(undefined);

										// Clear search results when input changes
										// This happens when user types, clears, or enters a valid KVK
										if (searchResults.length > 0) {
											setSearchResults([]);
										}

										// Clear status messages (info/error) when user starts typing
										if (submitStatus.message) {
											setSubmitStatus({ type: '', message: '' });
										}

										// If valid KVK is entered, clear results immediately
										if (isValidKvKNumber(newValue)) {
											setSearchResults([]);
										}
									}}
									disabled={isSearching}
									data-testid="modal-kvk-search-input"
									autoComplete="off"
									aria-invalid={!!searchError}
									className="flex-1 rounded-r-none border-r-0"
								/>
								<Button
									type="submit"
									disabled={isSearching}
									data-testid="modal-kvk-search-button"
									className="w-12 shrink-0 rounded-tl-none! rounded-bl-none!"
								>
									{isSearching ? (
										<Spinner size="sm" variant="current" visible={isSearching} />
									) : isValidKvKNumber(searchQuery) ? (
										<Check className="h-4 w-4" />
									) : (
										<Search className="h-4 w-4" />
									)}
								</Button>
							</div>
							<FieldDescription>{t('modals.kvk.unifiedSearchHint')}</FieldDescription>
							{searchError && <FieldError>{searchError}</FieldError>}
						</Field>
					</form>

					{submitStatus.message && (
						<Alert
							variant={
								submitStatus.type === 'success'
									? 'success'
									: submitStatus.type === 'info'
										? 'info'
										: 'destructive'
							}
							className="mb-4"
						>
							<AlertDescription>{submitStatus.message}</AlertDescription>
						</Alert>
					)}

					{searchResults.length > 0 && (
						<div className="mb-4 text-left">
							<h3 className="text-xl lg:text-xl font-bold mb-3">{t('modals.kvk.searchResults')}</h3>
							<div
								className="border border-input rounded-lg divide-y divide-input"
								data-testid="modal-kvk-search-results"
							>
								{searchResults.map((company) => (
									<div
										key={company.kvk_nummer}
										role="button"
										tabIndex={0}
										className="w-full flex justify-between items-center p-5 text-left hover:bg-accent transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 first:rounded-t-lg last:rounded-b-lg disabled:opacity-50 disabled:pointer-events-none"
										onClick={() => !isSearching && handleSelectCompany(company)}
										onKeyDown={(e) => {
											if ((e.key === 'Enter' || e.key === ' ') && !isSearching) {
												e.preventDefault();
												handleSelectCompany(company);
											}
										}}
										aria-disabled={isSearching}
										data-testid={`modal-kvk-select-${company.kvk_nummer}`}
									>
										<div className="mr-4">
											<span className="block text-sm font-bold text-foreground">
												{company.bedrijfsnaam}
											</span>
											<span className="text-xs text-muted-foreground">
												{company.kvk_nummer} - {company.plaats}
												{company.straatnaam && ` - ${company.straatnaam}`}
											</span>
										</div>
										<span className="shrink-0">
											<Button variant="outline" size="sm" asChild>
												<span>{t('modals.kvk.selectButton')}</span>
											</Button>
										</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default ModalKvK;
